/**
 * Global session / lifecycle guard (app-wide).
 * sessionToken invalidates all async work: before any async side effect (audio, mic,
 * setTimeout, promises), capture sessionId (getCurrentSessionToken) and abort
 * immediately if it no longer matches (isTokenCurrent). Used in: practice tests,
 * chat/follow-ups, voice (TTS + STT), skip/continue, analytics debounces.
 */

import { useRef, useCallback, useEffect } from 'react';
import { logger } from './logger';

let sessionToken = 0;

/** Create a new session token. Call when starting a new session or navigation. */
export function createSessionToken(): number {
  sessionToken += 1;
  logger.lifecycle('asyncGuard', 'token_created', { token: sessionToken });
  return sessionToken;
}

/** Return current token (for comparison). */
export function getCurrentSessionToken(): number {
  return sessionToken;
}

/** True if the given token is still current. */
export function isTokenCurrent(token: number): boolean {
  return token === sessionToken;
}

/**
 * Run an async function only if the token is still current after await points.
 * Call at the start of async flows: const token = getCurrentSessionToken();
 * Then before any setState/dispatch after await: if (!isTokenCurrent(token)) return;
 */
export function useSessionToken(): {
  token: number;
  isCurrent: () => boolean;
  create: () => number;
} {
  const tokenRef = useRef(sessionToken);
  const isCurrent = useCallback(() => tokenRef.current === sessionToken, []);
  const create = useCallback(() => {
    tokenRef.current = createSessionToken();
    return tokenRef.current;
  }, []);
  useEffect(() => {
    tokenRef.current = sessionToken;
  }, []);
  return { token: tokenRef.current, isCurrent, create };
}

/**
 * Wrapper: run async fn; after each await, abort if token is stale.
 * Usage: runWithToken(async (abortIfStale) => { ... await x(); abortIfStale(); ... })
 */
export async function runWithToken<T>(
  fn: (abortIfStale: () => void) => Promise<T>
): Promise<T | undefined> {
  const token = getCurrentSessionToken();
  const abortIfStale = () => {
    if (!isTokenCurrent(token)) {
      logger.lifecycle('asyncGuard', 'abort_stale', { token });
      throw new Error('STALE_TOKEN');
    }
  };
  try {
    return await fn(abortIfStale);
  } catch (e) {
    if (e instanceof Error && e.message === 'STALE_TOKEN') return undefined;
    throw e;
  }
}
